// components/Chat/cableClient.ts
import { createConsumer } from "@rails/actioncable";

let consumer = null;
let consumerIdentity = null;
const subs = new Map();

const logWs = (...args) => {
  if (typeof console !== "undefined") {
    console.log("[WS]", ...args);
  }
};

export function getConsumer(url) {
  if (!consumer) {
    consumer = createConsumer(url);
  }
  return consumer;
}

// The cable connection authenticates from the session cookie exactly once, when
// the socket opens, and `consumer` is cached for the life of the page. So after
// a client-side login the socket keeps the *previous* account's identity: it
// listens on that account's streams and never hears the current one's. Call
// this whenever the signed-in identity is known; a change tears the socket down
// so the next subscription reconnects and re-authenticates.
export function syncCableIdentity(identity) {
  const next = identity ?? null;
  if (consumerIdentity !== null && consumerIdentity !== next) {
    logWs("Identity changed, reconnecting", consumerIdentity, "->", next);
    disconnectAll();
  }
  consumerIdentity = next;
}

function clearHeartbeat(sub) {
  if (sub && sub._aliveTimer) {
    clearInterval(sub._aliveTimer);
    sub._aliveTimer = null;
  }
}

const PREVIEW_ACK_TIMEOUT_MS = 2500;
const MAX_PREVIEW_RETRIES = 2;
const monotonicNow = () => globalThis.performance?.now?.() ?? Date.now();

function clearPreviewTimer(sub) {
  clearTimeout(sub._previewTimer);
  sub._previewTimer = null;
}

function waitForPreviewAck(sub) {
  clearPreviewTimer(sub);
  sub._previewTimer = setTimeout(() => {
    const pending = sub._previewPending;
    if (!pending || ["disposed", "rejected"].includes(sub._inciteState)) return;
    if (pending.retries >= MAX_PREVIEW_RETRIES) {
      sub._previewPending = null;
      return;
    }
    pending.retries += 1;
    // Reassert this same subscription. Do not unsubscribe a stream that may
    // still own a pending answer. ActionCable confirms repeated subscribes.
    sub.consumer?.subscriptions?.sendCommand(sub, "subscribe");
    if (sub._inciteState === "confirmed") sendPendingPreview(sub);
    else waitForPreviewAck(sub);
  }, PREVIEW_ACK_TIMEOUT_MS);
}

function sendPendingPreview(sub) {
  if (!sub._previewPending || sub._inciteState !== "confirmed") return;
  sub._rawPerform("preview_tickers", sub._previewPending.payload);
  waitForPreviewAck(sub);
}

function dispose(sub) {
  sub._inciteState = "disposed";
  clearHeartbeat(sub);
  clearPreviewTimer(sub);
  sub._previewPending = null;
  try { sub.unsubscribe(); } catch {}
}

function buildCallbacks(callbacks) {
  return {
    connected() {
      if (this._inciteState === "disposed") return;
      this._inciteState = "confirmed";
      callbacks?.connected?.call(this);
      // (re)start heartbeat here only after confirm
      clearHeartbeat(this);
      try { this.perform("alive"); } catch {}
      this._aliveTimer = setInterval(() => {
        // optional guard if consumer is gone
        if (!this.consumer || !this.identifier) return;
        try { this.perform("alive"); } catch {}
      }, 5000);
      sendPendingPreview(this);
    },
    disconnected() {
      if (this._inciteState === "disposed") return;
      this._inciteState = "disconnected";
      // socket level disconnect
      clearHeartbeat(this);
      callbacks?.disconnected?.call(this);
    },
    rejected() {
      if (this._inciteState === "disposed") return;
      this._inciteState = "rejected";
      clearHeartbeat(this);
      clearPreviewTimer(this);
      this._previewPending = null;
      callbacks?.rejected?.call(this);
    },
    received(data) {
      if (this._inciteState === "disposed") return;
      const ack = data?.ticker_preview_ack;
      if (ack && ack.request_id === this._previewPending?.requestId) {
        const elapsedMs = Math.round(monotonicNow() - this._previewPending.startedAt);
        clearPreviewTimer(this);
        this._previewPending = null;
        this._lastPreviewAck = { ...ack, client_elapsed_ms: elapsedMs };
      }
      callbacks?.received?.call(this, data);
    },
  };
}

function applyCallbacks(sub, callbacks) {
  const wrapped = buildCallbacks(callbacks);
  sub.connected = wrapped.connected;
  sub.disconnected = wrapped.disconnected;
  sub.rejected = wrapped.rejected;
  sub.received = wrapped.received;
}

export function ensureSubscription(key, params, callbacks, options = {}) {
  const { force = false } = options || {};
  const existing = subs.get(key);
  if (existing) {
    if (!force && !["disposed", "rejected"].includes(existing._inciteState)) {
      applyCallbacks(existing, callbacks);
      logWs("Reusing subscription", key);
      return existing;
    }
    logWs("Replacing subscription", key);
    // 🔑 stop old heartbeats first
    dispose(existing);
    subs.delete(key);
  }

  const subscription = getConsumer(params.url).subscriptions.create(
    params.identifier,
    buildCallbacks(callbacks)
  );

  subscription._inciteState ||= "connecting";
  subscription._rawPerform = subscription.perform.bind(subscription);
  subscription.perform = (action, payload) => {
    if (["disposed", "rejected"].includes(subscription._inciteState)) return false;
    if (action !== "preview_tickers") return subscription._rawPerform(action, payload);
    const data = payload?.data || payload || {};
    if (data.cancel || !data.query) {
      if (!data.request_id || data.request_id === subscription._previewPending?.requestId) {
        clearPreviewTimer(subscription);
        subscription._previewPending = null;
      }
      return subscription._inciteState === "confirmed" ? subscription._rawPerform(action, payload) : false;
    }
    clearPreviewTimer(subscription);
    subscription._previewPending = { payload, requestId: data.request_id, retries: 0, startedAt: monotonicNow() };
    if (subscription._inciteState === "confirmed") sendPendingPreview(subscription);
    else waitForPreviewAck(subscription);
    // Accepted for delivery; only ticker_preview_ack proves server admission.
    return true;
  };

  subs.set(key, subscription);
  return subscription;
}

export function dropSubscription(key, reason = null, expectedSubscription = null) {
  const sub = subs.get(key);
  if (sub && (!expectedSubscription || sub === expectedSubscription)) {
    dispose(sub);
    subs.delete(key);
  }
}

export function disconnectAll() {
  logWs("Disconnecting all subscriptions", subs.size);
  subs.forEach((sub) => {
    dispose(sub);
  });
  subs.clear();
  if (consumer) {
    try { consumer.disconnect(); } catch {}
    consumer = null;
  }
  consumerIdentity = null;
}
